import React, { useState, useRef, useEffect } from "react";
import "./verify-otp.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
    useGenerateEmailOtpMutation,
    useVerifyotpMutation,
    useSendPhoneOtpMutation,
    useVerifyPhoneOtpMutation,
} from "../../../utils/apiSlice";
import { loginSuccess } from "../../../utils/authSlice";
import { triggerCoins } from "../../canvasConfict";

const VerifyOtp = () => {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [errors, setErrors] = useState([false, false, false, false, false, false]);
    const [countdown, setCountdown] = useState(30); // 30 seconds timer
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [captchaValue, setCaptchaValue] = useState("");
    const [userCaptcha, setUserCaptcha] = useState("");

    const [verifyOtpMutation] = useVerifyotpMutation();
    const [generateEmailOtpMutation] = useGenerateEmailOtpMutation();
    const [verifyPhoneOtpMutation] = useVerifyPhoneOtpMutation();
    const [sendPhoneOtpMutation] = useSendPhoneOtpMutation();
    const dispatch = useDispatch();

    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const navigate = useNavigate();
    const location = useLocation();

    const { method, contact, purpose } = location.state || { method: 'email', contact: 'your registered email', purpose: "REGISTRATION" };
    const otpPurpose = purpose || "REGISTRATION";
    const isMobile = method === "mobile";
    const [mobileFullName, setMobileFullName] = useState("");

    const persistSessionFromPayload = (payloadData: any, storage: Storage) => {
        const tokenString = payloadData?.token?.accessToken || payloadData?.token;
        const patientProfile = payloadData?.patientProfile || {};
        const safeRoles = patientProfile?.role || payloadData?.roles || "USER";
        const familyMembers = payloadData?.familyMembers || [];
        storage.setItem("token", tokenString);
        storage.setItem("userData", JSON.stringify(patientProfile));
        storage.setItem("familyMembers", JSON.stringify(familyMembers));
        const rolesArray =
            typeof safeRoles === "string" && safeRoles.includes(",") ? safeRoles.split(",") : [safeRoles];
        storage.setItem("roles", JSON.stringify(rolesArray));
        dispatch(loginSuccess(tokenString));
    };

    const tryFinishMobileVerify = (response: any) => {
        const payloadData = response?.data ?? response;
        const tokenString = payloadData?.token?.accessToken || payloadData?.token;
        if (tokenString) {
            persistSessionFromPayload(payloadData, localStorage);
            toast.success(response?.message || "Login successful");
            navigate("/dashboard");
            return true;
        }
        return false;
    };

    const getOtpCountdownSeconds = (resp: any, fallbackSeconds = 30) => {
        const rawSeconds =
            resp?.expiresInSeconds ??
            resp?.data?.expiresInSeconds ??
            resp?.expires_in_seconds ??
            resp?.data?.expires_in_seconds;

        const rawMinutes =
            resp?.expiresInMinutes ??
            resp?.data?.expiresInMinutes ??
            resp?.expires_in_minutes ??
            resp?.data?.expires_in_minutes;

        const seconds = typeof rawSeconds === "string" ? parseInt(rawSeconds, 10) : rawSeconds;
        if (typeof seconds === "number" && seconds > 0) return Math.min(seconds, fallbackSeconds);

        const minutes = typeof rawMinutes === "string" ? parseFloat(rawMinutes) : rawMinutes;
        if (typeof minutes === "number" && minutes > 0) return Math.min(Math.floor(minutes * 60), fallbackSeconds);

        return fallbackSeconds;
    };

    useEffect(() => {
        if (!contact || contact === "your registered email") return;

        if (isMobile) {
            sendPhoneOtpMutation({ phoneNumber: contact })
                .unwrap()
                .then((response: any) => {
                    if (response?.success || response) {
                        setCountdown(getOtpCountdownSeconds(response, 30));
                    }
                })
                .catch((err:any) => console.error("Initial phone OTP send failed:", err));
            return;
        }

        generateEmailOtpMutation({ email: contact })
            .unwrap()
            .then((response: any) => {
                if (response?.success || response) {
                    setCountdown(getOtpCountdownSeconds(response, 30));
                }
            })
            .catch((err:any) => console.error("Initial OTP send failed:", err));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    // Generate Captcha Code
    const generateCaptcha = () => {
        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
        let result = "";
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setCaptchaValue(result);
    };

    const handleChange = (value: string, index: number) => {
        if (isNaN(Number(value))) return;

        const newOtp = [...otp];
        newOtp[index] = value.substring(value.length - 1);
        setOtp(newOtp);

        const newErrors = [...errors];
        newErrors[index] = false;
        setErrors(newErrors);

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        const joined = newOtp.join("");
        if (joined.length === 6 && !newOtp.includes("")) {
            void handleVerify(joined);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerify = async (otpOverride?: string) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        if (!showCaptcha) {
            const otpValue = otpOverride ?? otp.join("");
            if (otpValue.length !== 6) {
                toast.error("Please enter the complete OTP");
                setIsSubmitting(false);
                return;
            }
            setErrors(new Array(6).fill(false));

            try {
                if (isMobile) {
                    const name = mobileFullName.trim();
                  
                    const response: any = await verifyPhoneOtpMutation({
                        phoneNumber: contact,
                        otp: otpValue,
                        name,
                    }).unwrap();

                    if (tryFinishMobileVerify(response)) {
                        setIsSubmitting(false);
                        return;
                    }
                    if (response?.success || response) {
                        toast.success(response?.message || "OTP Verified! Please complete security check.");
                        generateCaptcha();
                        setShowCaptcha(true);
                    }
                     if(response?.data?.coinSummary?.coinsEarned){
                            triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0 ,response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)
                    
                          }
                } else {
                    const response: any = await verifyOtpMutation({
                        email: contact,
                        otp: otpValue,
                        purpose: otpPurpose,
                    }).unwrap();

                    if (response?.success || response) {
                        toast.success(response?.message || "OTP Verified! Please complete security check.");
                        generateCaptcha();
                        setShowCaptcha(true);
                    }
                }
            } catch (error: any) {
                if (error?.originalStatus === 200 || error?.status === 200 || error?.status === 202) {
                    if (isMobile) {
                        const payload = error?.data?.data ?? error?.data ?? {};
                        if (tryFinishMobileVerify({ data: payload, message: error?.data?.message })) {
                            setIsSubmitting(false);
                            return;
                        }
                    }
                    toast.success(error?.data?.message || "OTP Verified! Please complete security check.");
                    generateCaptcha();
                    setShowCaptcha(true);
                } else {
                    setErrors(new Array(6).fill(true));
                    const errorMsg = error?.data?.message || error?.data?.detail || error?.message || "Invalid OTP. Please try again.";
                    const normalized = typeof errorMsg === "string" ? errorMsg.toLowerCase() : String(errorMsg);
                    if (
                        normalized.includes("account with this email already exists") ||
                        normalized.includes("account with this phone number already exists") ||
                        normalized.includes("account with this mobile already exists") ||
                        normalized.includes("account already exists")
                    ) {
                        // Show toast only on register page (after navigation).
                        sessionStorage.setItem("pendingRegisterToast", errorMsg);
                        setIsSubmitting(false);
                        navigate("/register");
                        return;
                    }
                    toast.error(errorMsg);
                    console.error("Error verifying OTP:", error);
                }
            }
        }
        //  Verify Captcha
        else {
            if (userCaptcha.toUpperCase() !== captchaValue) {
                toast.error("Invalid Captcha. Please try again.");
                generateCaptcha();
                setUserCaptcha("");
                setIsSubmitting(false);
                return;
            }
            toast.success("Verification Successful!");
            navigate("/dashboard");
        }
        setIsSubmitting(false);
    };

    const handleResend = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (countdown === 0) {
            try {
                const response: any = isMobile
                    ? await sendPhoneOtpMutation({ phoneNumber: contact }).unwrap()
                    : await generateEmailOtpMutation({ email: contact }).unwrap();

                if (response?.success || response) {
                    setCountdown(getOtpCountdownSeconds(response, 30));
                    toast.info(response?.message || "OTP has been resent!");
                }
            } catch (error: any) {
                if (error?.originalStatus === 200 || error?.status === 200 || error?.status === 201) {
                    setCountdown(getOtpCountdownSeconds(error?.data, 30));
                    toast.info(error?.data?.message || "OTP has been resent!");
                } else {
                    console.error("Error generating OTP:", error);
                    let errorMsg = error?.data?.message || error?.data?.detail || error?.message || "Failed to resend OTP.";
                    if (errorMsg === "TypeError: Failed to fetch") {
                        errorMsg = "Network Error: Failed to connect to the server.";
                    }
                    toast.error(errorMsg);
                }
            }
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins < 10 ? "0" + mins : mins}:${secs < 10 ? "0" + secs : secs}`;
    };

    return (
        <div className="container-fluid h-p100 auth-container">
            <div className="row align-items-center justify-content-md-center h-p100">
                <div className="col-12">
                    <div className="row justify-content-center g-0">
                        <div className="">
                            <div className="rounded10 shadow-lg translucent-card p-40" style={{ position: "relative" }}>
                                {isSubmitting && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            background: "rgba(255, 255, 255, 0.75)",
                                            zIndex: 10,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: 10,
                                        }}
                                    >
                                        <div style={{ fontWeight: 600 }}>Please wait...</div>
                                    </div>
                                )}
                                <div className="content-top-agile p-20 pb-0 text-center">
                                    <h2 >
                                        {showCaptcha ? "Security Check" : "Verify Your Account"}
                                    </h2>

                                    <p className="mb-0 text-dark fw-medium opacity-75">
                                        {showCaptcha ? "Please verify you are human." : "Secure your account with the code sent to you."}
                                    </p>
                                    {!showCaptcha && (
                                        <p className="text-dark mt-2 fs-16 opacity-75">
                                            Enter the 6-digit code sent to your {method === 'email' ? 'email' : 'mobile'}: <br />
                                            <span className="fw-bold text-dark opacity-100">{contact}</span>
                                        </p>
                                    )}
                                </div>

                                <div className="form-input-container mt-30">
                                    {!showCaptcha ? (
                                        <>
                                            {isMobile ? (
                                                <div className="form-group mb-4">
                                                    <label htmlFor="verify-otp-full-name" className="form-label text-start d-block mb-1">
                                                        Full name
                                                    </label>
                                                    <input
                                                        id="verify-otp-full-name"
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Enter your full name"
                                                        value={mobileFullName}
                                                        onChange={(e) => setMobileFullName(e.target.value)}
                                                        autoComplete="name"
                                                    />
                                                </div>
                                            ) : null}
                                            <div className="otp-container d-flex justify-content-between mb-30">
                                                {otp.map((digit, index) => (
                                                    <input
                                                        key={index}
                                                        type="text"
                                                        maxLength={1}
                                                        value={digit}
                                                        ref={(el) => { inputRefs.current[index] = el; }} // Fixed ref callback
                                                        onChange={(e) => handleChange(e.target.value, index)}
                                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                                        className={`form-control otp-input ${errors[index] ? "error" : ""} ${digit ? "filled" : ""}`}
                                                    />
                                                ))}
                                            </div>

                                            <div className="auth-otp-resend mt-30">
                                                <p className="auth-otp-resend__hint">Didn&apos;t receive the code?</p>
                                                {countdown > 0 ? (
                                                    <div className="auth-otp-resend__timer" role="status" aria-live="polite" aria-atomic="true">
                                                        <span className="auth-otp-resend__timer-label">Resend in</span>
                                                        <span className="auth-otp-resend__timer-value">{formatTime(countdown)}</span>
                                                    </div>
                                                ) : (
                                                    <a href="#" onClick={handleResend} className="auth-otp-resend__link">
                                                        Resend code
                                                    </a>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="captcha-step fade-in">
                                            <div className="d-flex align-items-center justify-content-center mb-4">
                                                <div className="captcha-display p-3 bg-light rounded border d-flex align-items-center justify-content-between" style={{ minWidth: '200px' }}>
                                                    <span className="fs-24 fw-bold text-primary letter-spacing-5 font-monospace unselectable">
                                                        {captchaValue.split('').map((char, i) => (
                                                            <span key={i} style={{ transform: `rotate(${Math.random() * 20 - 10}deg)`, display: 'inline-block' }}>{char}</span>
                                                        ))}
                                                    </span>
                                                    <button className="btn btn-link text-muted p-0 ms-3" onClick={generateCaptcha}>
                                                        <i className="fa fa-refresh fs-20"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="form-group mb-30">
                                                <input
                                                    type="text"
                                                    className="form-control text-center fs-20 fw-bold letter-spacing-2"
                                                    placeholder="Enter Captcha Code"
                                                    value={userCaptcha}
                                                    onChange={(e) => setUserCaptcha(e.target.value)}
                                                    maxLength={6}
                                                    style={{ height: '50px', borderRadius: '12px' }}
                                                    autoFocus
                                                />
                                            </div>

                                            <div className="text-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-verify fw-bold shadow-sm"
                                                    onClick={() => void handleVerify()}
                                                >
                                                    COMPLETE VERIFICATION
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyOtp;
